import { useState } from 'react';
import {
	Box,
	Button,
	Flex,
	HStack,
	NumberInput,
	NumberInputField,
	SimpleGrid,
	Spinner,
	Switch,
	Text,
	Textarea,
	Tooltip,
} from '@chakra-ui/react';
import { RiRefreshLine } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { EmptyState, SectionCard, StatusBadge } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import { useListSendersQuery, useRefreshSendersMutation, usePatchSenderMutation } from 'api/metaWhatsappApi';
import campaignError from '../campaignErrors';

/**
 * The pool board: what each number can carry today, and whether it should.
 *
 * ★ CAPACITY IS A PERCENTAGE OF THE NUMBER'S LIVE TIER, NOT A FLAT FIGURE.
 * WhatsApp raises a number's tier only when it uses at least half its limit for
 * a week at good quality, so a flat "500 a day" on a 10,000 number freezes it
 * there for ever. The absolute cap exists for an operator who genuinely wants a
 * ceiling, and it is deliberately the second field, not the first.
 *
 * ★ A BLOCKED NUMBER SAYS WHY. "Unavailable" with no reason is the version of
 * this screen nobody can act on.
 */

/**
 * What the half-open probe is doing, in words.
 *
 * ★ A STOPPED NUMBER IS NOT A DEAD ONE, AND THE CARD USED TO IMPLY IT WAS.
 * After the breaker trips on a rate, the number is stood down and then given
 * one short, deliberate window to prove itself — a handful of sends, watched.
 * Without this line an administrator sees "Stopped automatically" with no clock
 * on it, assumes the number needs rescuing, and un-pauses it by hand, which is
 * precisely the thing the stand-down exists to prevent.
 *
 * Only a RATE-caused stop earns a probe; a quality-caused one waits for Meta,
 * and `halfOpenAt` is absent there — so no clock is promised that will not come.
 */
function probeText(breaker) {
	if (!breaker?.halfOpenAt) return null;
	const at = new Date(breaker.halfOpenAt);
	if (Number.isNaN(at.getTime())) return null;

	const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	return at > new Date()
		? `Trying again by itself at ${time}. No need to intervene.`
		: `Testing a few sends now to see whether it has recovered.`;
}

const BLOCK_REASON = {
	admin_paused: 'Paused by an administrator',
	quality_red: 'WhatsApp rated this number red',
	breaker_critical: 'Stopped automatically after repeated failures',
	throttled: 'WhatsApp asked us to slow down',
	peer_unreachable: 'The WhatsApp service could not be reached',
	accountRestricted: 'WhatsApp has restricted this account',
	tokenNeedsReauth: 'The connection needs re-authenticating',
	metaDisconnected: 'Disconnected from WhatsApp',
};

const num = (v) => Number(v ?? 0).toLocaleString();

/** Why a number is not sending, in words. */
function reasonText(reason) {
	if (!reason) return null;
	if (BLOCK_REASON[reason]) return BLOCK_REASON[reason];
	// `throttled:131048`, `account_blocked:131042`, `blocked:tokenNeedsReauth`…
	const [head, detail] = String(reason).split(':');
	const base = BLOCK_REASON[head] ?? BLOCK_REASON[detail] ?? head.replace(/_/g, ' ');
	return detail && Number.isNaN(Number(detail)) ? BLOCK_REASON[detail] ?? base : base;
}

function SenderCard({ sender, onPatch, isSaving }) {
	const ui = useCampaignUI();
	const capacity = sender.capacity ?? {};
	const meta = sender.meta ?? {};
	const admin = sender.admin ?? {};

	const [capPercent, setCapPercent] = useState(admin.capPercent ?? '');
	const [note, setNote] = useState(admin.note ?? '');
	const probe = probeText(sender.breaker);
	const used = capacity.used ?? 0;
	const cap = capacity.cap ?? 0;
	const share = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;

	return (
		<Box bg={ui.surface} borderWidth='1px' borderColor={ui.border} borderRadius='xl' boxShadow={ui.shadowCard} p={5}>
			<Flex justify='space-between' align='flex-start' mb={3} gap={3}>
				<Box minW='0'>
					<Text fontWeight='semibold' color={ui.textPrimary}>
						{sender.displayPhoneNumber || sender.phoneNumberId}
					</Text>
					<Text fontSize='xs' color={ui.textMuted}>
						{meta.tierName ? meta.tierName.replace('TIER_', 'Tier ') : 'Tier not reported'}
						{meta.qualityRating ? ` · quality ${meta.qualityRating.toLowerCase()}` : ''}
					</Text>
				</Box>
				<StatusBadge status={capacity.blocked ? 'BLOCKED' : sender.breaker?.state ?? 'HEALTHY'} />
			</Flex>

			{capacity.blocked ? (
				<Text fontSize='sm' color={ui.warning} mb={3}>
					{reasonText(capacity.reason) ?? 'Not sending right now'}
				</Text>
			) : (
				<Box mb={3}>
					<Flex justify='space-between' mb={1}>
						<Text fontSize='xs' color={ui.textMuted}>
							Used today
						</Text>
						<Text fontSize='xs' color={ui.textSecondary}>
							{num(used)} of {num(cap)}
						</Text>
					</Flex>
					<Box h='4px' bg={ui.surfaceSubtle} borderRadius='full' overflow='hidden'>
						<Box h='100%' w={`${share}%`} bg={ui.accent} />
					</Box>
					<Text fontSize='xs' color={ui.textMuted} mt={1}>
						{num(capacity.remaining)} left
					</Text>
				</Box>
			)}

			<Flex align='center' justify='space-between' gap={3} mb={3}>
				<Tooltip
					hasArrow
					label='A share of whatever tier WhatsApp currently allows. Leave it at the tenant default unless this number needs to be held back.'
				>
					<Text fontSize='sm' color={ui.textSecondary}>
						Daily cap
					</Text>
				</Tooltip>
				<HStack spacing={2}>
					<NumberInput
						size='sm'
						maxW='90px'
						min={1}
						max={100}
						value={capPercent}
						onChange={(v) => setCapPercent(v)}
					>
						<NumberInputField
							bg={ui.inputBg}
							borderColor={ui.border}
							color={ui.textPrimary}
							placeholder='default'
						/>
					</NumberInput>
					<Text fontSize='sm' color={ui.textMuted}>
						%
					</Text>
					<Button
						size='xs'
						variant='neutralOutline'
						borderColor={ui.border}
						color={ui.textPrimary}
						isDisabled={isSaving || String(capPercent) === String(admin.capPercent ?? '')}
						onClick={() =>
							onPatch(sender.phoneNumberId, {
								capPercent: capPercent === '' ? null : Number(capPercent),
							})
						}
					>
						Save
					</Button>
				</HStack>
			</Flex>

			<Flex align='center' justify='space-between'>
				<Tooltip hasArrow label='A paused number carries nothing. Campaigns route around it on the next tick.'>
					<Text fontSize='sm' color={ui.textSecondary}>
						Paused
					</Text>
				</Tooltip>
				<Switch
					size='sm'
					
					isChecked={Boolean(admin.paused)}
					isDisabled={isSaving}
					onChange={(e) => onPatch(sender.phoneNumberId, { paused: e.target.checked })}
				/>
			</Flex>

			{probe && (
				<Box mt={3} pt={3} borderTopWidth='1px' borderColor={ui.borderSubtle}>
					<Text fontSize='xs' color={ui.textSecondary}>
						{probe}
					</Text>
				</Box>
			)}

			{/* ★ THE NOTE IS THE ONLY PLACE THE *WHY* CAN LIVE. Everything else on
			    this card is a number or a machine verdict; "held at 20% until the
			    new template is approved" is not derivable from any of them, and
			    without it the next administrator simply undoes the cap. */}
			<Box mt={3} pt={3} borderTopWidth='1px' borderColor={ui.borderSubtle}>
				<Text fontSize='xs' color={ui.textMuted} mb={1}>
					Note for the team
				</Text>
				<HStack spacing={2} align='flex-start'>
					<Textarea
						size='sm'
						rows={2}
						resize='vertical'
						maxLength={500}
						placeholder='Why is this number capped, paused or being watched?'
						value={note}
						onChange={(e) => setNote(e.target.value)}
						borderColor={ui.border}
						color={ui.textPrimary}
					/>
					<Button
						size='sm'
						variant='neutralOutline'
						borderColor={ui.border}
						color={ui.textPrimary}
						isDisabled={isSaving || note === (admin.note ?? '')}
						onClick={() => onPatch(sender.phoneNumberId, { note })}
					>
						Save
					</Button>
				</HStack>
			</Box>
		</Box>
	);
}

export default function SenderPool() {
	const ui = useCampaignUI();
	const notify = useAppToast();

	const { data: senders, isLoading, isError, refetch } = useListSendersQuery();
	const [refresh, { isLoading: isRefreshing }] = useRefreshSendersMutation();
	const [patch, { isLoading: isSaving }] = usePatchSenderMutation();

	const onPatch = async (phoneNumberId, body) => {
		try {
			await patch({ phoneNumberId, ...body }).unwrap();
			notify.success('Saved');
		} catch (err) {
			notify.error('Could not save', campaignError(err));
		}
	};

	const onRefresh = async () => {
		try {
			await refresh().unwrap();
			notify.success('Refreshed from WhatsApp');
		} catch (err) {
			notify.error('Could not refresh', campaignError(err));
		}
	};

	if (isLoading) {
		return (
			<Flex justify='center' py={12}>
				<Spinner color={ui.accentInk} />
			</Flex>
		);
	}

	const list = Array.isArray(senders) ? senders : [];

	return (
		<SectionCard
			title='Sender pool'
			subtitle='What each number may carry today. Campaigns pick a number at send time from whatever is healthy.'
			actions={
				<Button
					size='sm'
					leftIcon={<RiRefreshLine />}
					variant='neutralOutline'
					borderColor={ui.border}
					color={ui.textPrimary}
					isLoading={isRefreshing}
					onClick={onRefresh}
				>
					Refresh from WhatsApp
				</Button>
			}
		>
			{isError ? (
				<EmptyState title='We could not load the pool' description='Try refreshing.' action={
					<Button size='sm' onClick={refetch} variant='neutralOutline' borderColor={ui.border} color={ui.textPrimary}>
						Retry
					</Button>
				} />
			) : !list.length ? (
				<EmptyState
					title='No numbers connected'
					description='Connect a WhatsApp number on the Meta WhatsApp tab and it appears here.'
				/>
			) : (
				<SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
					{list.map((sender) => (
						<SenderCard key={sender.phoneNumberId} sender={sender} onPatch={onPatch} isSaving={isSaving} />
					))}
				</SimpleGrid>
			)}
		</SectionCard>
	);
}
